import DOMPurify from 'dompurify'
import { splitMathSegments } from './mathTextSegments'

// Everything rendered through MixedContentPreview goes through a
// markdown/HTML/MathJax pipeline that ends in `dangerouslySetInnerHTML`, and
// some of that content is written by students or is model output derived from a
// photo they uploaded. This module is the choke point that makes such a string
// inert before the renderer ever sees it.
//
// Two surfaces need covering, because they are parsed by different engines:
//
// 1. Prose is parsed by markdown-it. Raw HTML is already disabled there
//    (mathpix-markdown-it's `htmlTags` defaults to false, which is markdown-it's
//    `html: false`) and markdown-it's own link validation rejects
//    `javascript:`/`vbscript:`/`data:` URLs. DOMPurify is applied anyway so the
//    guarantee belongs to us rather than to a transitive default that a version
//    bump could flip.
// 2. Math is parsed by MathJax, which markdown-it never inspects.
//    mathpix-markdown-it enables MathJax's `html` TeX package, whose `\href`,
//    `\class`, `\style` and `\cssId` macros emit real anchors, class names and
//    inline CSS from inside `$…$`. Those are the only genuinely active
//    constructs a message can reach, so they are neutralized by name.

// Control sequences that can emit markup, styling, a URL, or load more TeX
// packages. The whole backslash run is dropped, which leaves the name rendering
// as inert literal text; dropping only one would turn the line break in
// `\\href` into a live `\href`. A TeX name ends at the first non-letter, so
// `\href1` is caught while legitimate macros that merely start with one of
// these names (`\datavisualization`, `\classic`) stay intact. Mirrors
// ACTIVE_TEX_MACROS in functions/explainVisuals.js.
const ACTIVE_MATH_MACROS = new RegExp(
  '\\\\+(' + [
    // MathJax `html` package: anchors, class names, ids and inline CSS.
    'href', 'class', 'style', 'cssId',
    // KaTeX spellings of the same, in case the renderer is ever swapped.
    'htmlClass', 'htmlStyle', 'htmlId', 'htmlData', 'data',
    // Sets MathML attributes directly, bypassing the macro layer entirely.
    'mmlToken',
    // Loads further TeX packages, i.e. re-enables anything blocked above.
    'require',
    // Reach outside the expression: files, images, raw driver output.
    'includegraphics', 'input', 'write', 'openout', 'special'
  ].join('|') + ')(?![A-Za-z])',
  'g'
)

const TEXT_ONLY = {
  ALLOWED_TAGS: [],
  ALLOWED_ATTR: [],
  // Tags are dropped but the text they wrapped survives, so a student who
  // types an angle bracket still sees their sentence.
  KEEP_CONTENT: true
}

/**
 * Makes mixed prose/LaTeX safe to hand to MixedContentPreview.
 *
 * Prose keeps its meaning: DOMPurify escapes stray `<` and `&` rather than
 * deleting them, and markdown-it decodes those entities back to visible text.
 * Math keeps its notation; only the macros that can emit active content are
 * defanged.
 */
export const sanitizeMathContent = (input) => {
  const text = typeof input === 'string' ? input : ''
  if (!text) return ''

  // Run over the whole string, not just the math segments: MixedContentPreview
  // can promote a bare LaTeX-looking line into math, so a macro sitting in what
  // is currently prose may still reach MathJax.
  const defanged = text.replace(ACTIVE_MATH_MACROS, '$1')

  return splitMathSegments(defanged)
    .map((segment) => (
      segment.type === 'math'
        ? segment.value
        : DOMPurify.sanitize(segment.value, TEXT_ONLY)
    ))
    .join('')
}
